/* eslint-disable import/order */
// Core & Essential Dependencies
import * as Sentry from '@sentry/react';
import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import './App.css';

// MUI Components & Theme
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

// Project Configs & Contexts
import { ConfigContext } from 'context';
import ExperimentRoute from 'experiments/ExperimentRoute';
import { EXPERIMENTS } from 'experiments/registry';

// Import the partner configuration hook instead of the static config
import { getPartnerTheme, usePartnerConfig } from 'partners';
import { RouteProductTheme } from 'themes/ProductThemeProvider';

// Utility Functions & Analytics
import { useMediaQuery } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import InstaLoader from 'components/LoaderComponent/InstaLoader';
import ScrollToTop from 'components/ScrollFromTop';
import InternetStatusRoute from 'pages/InternetStatusRoute';
// AppBridge is the entry point of every in-app webview session: import it
// statically so it renders straight from the main bundle instead of costing
// an extra chunk round trip before the loader even appears.
import AppBridgePage from 'pages/AppBridge';
import { ROUTES, getRoute } from 'utils/constants';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';

// Privacy Policy and Terms of Service pages
const PrivacyPolicy = React.lazy(() => import('pages/PrivacyPolicy'));

const TermsOfService = React.lazy(
  () => import('pages/TermsAndService/TermsAndServices')
);

const SupportPage = React.lazy(() => import('pages/Support'));

// Authentication-related pages
const LoginScreen = React.lazy(() => import('./pages/LoginScreen'));

// Public-facing application entry points
// const LaunchApp = React.lazy(() => import('pages/LaunchApp'));

// 404 Not Found page
const NotFound = React.lazy(() => import('pages/NotFound/NotFound'));

// User account overview and settings

// Account deletion request and verification
const AccountDeletionRequest = React.lazy(
  () => import('pages/AccountDeletion/request')
);
const VerifyDeleteRequest = React.lazy(
  () => import('pages/AccountDeletion/verifyRequest')
);

// Landing page for the reset link in the backend's password-reset email
const ResetPassword = React.lazy(() => import('pages/ResetPassword'));

const MaxCameraCountReached = React.lazy(
  () => import('pages/MaxCameraCountReached')
);

const CustomerSupport = React.lazy(() => import('pages/CustomerSupport'));

const FAQPage = React.lazy(() => import('pages/FaqPage'));

const CancellationRefundPolicy = React.lazy(
  () => import('pages/CancellationRefundPolicy')
);

// /addCameraInfo: the camera naming page, or a redirect to the hub flow when
// the paired device is a hub (the app opens this route for both).
const AddCameraInfoEntry = React.lazy(
  () => import('pages/v1/AddCameraInfoEntry')
);

const AddHubInfoV1 = React.lazy(() => import('pages/v1/AddHubInfoV1'));

const BirdFeederUpgradeSubscriptionV1 = React.lazy(
  () => import('pages/v1/BirdFeederUpgradeSubscriptionV1')
);

const BuyBirdFeederSubscriptionV1 = React.lazy(
  () => import('pages/v1/BuyBirdFeederSubscriptionV1')
);

// Pet Camera Start

const PetCameraUpgradeSubscriptionV1 = React.lazy(
  () => import('pages/v1/PetCameraUpgradeSubscriptionV1')
);

const BuyPetCameraSubscriptionV1 = React.lazy(
  () => import('pages/v1/BuyPetCameraSubscriptionV1')
);

// Pet Camera End

// Baby Camera Start

const BuyBabyCameraSubscriptionV1 = React.lazy(
  () => import('pages/v1/BuyBabyCameraSubscriptionV1')
);

const BabyCameraUpgradeSubscriptionV1 = React.lazy(
  () => import('pages/v1/BabyCameraUpgradeSubscriptionV1')
);

// Baby Camera End

// Trail Camera Start

const TrailCameraUpgradeSubscriptionV1 = React.lazy(
  () => import('pages/v1/TrailCameraUpgradeSubscriptionV1')
);

const BuyTrailCameraSubscriptionV1 = React.lazy(
  () => import('pages/v1/BuyTrailCameraSubscriptionV1')
);

// Trail Camera End

const CheckoutScreenV1 = React.lazy(() => import('pages/v1/CheckoutSessionV1'));

const DashboardV1 = React.lazy(() => import('pages/v1/DashboardV1'));

const HomeBuySubscriptionV1 = React.lazy(
  () => import('pages/v1/HomeBuySubscriptionV1')
);

const HomeBuyDataSubscriptionV1 = React.lazy(
  () => import('pages/v1/HomeBuyDataSubscriptionV1')
);

const SubscriptionDetailsV1 = React.lazy(
  () => import('pages/v1/SubscriptionDetailsV1')
);

const FourGOneTimeDataPlanScreen = React.lazy(
  () => import('pages/v1/BuyOneTimeDataV1/index')
);

const HomeSubscriptionListV1 = React.lazy(
  () => import('pages/v1/HomeSubscriptionsDetailsV1/HomeSubscriptionListV1')
);

const SkipTrialV1 = React.lazy(() => import('pages/v1/SkipTrialV1'));
const HomeTrialSkipV1 = React.lazy(() => import('pages/v1/HomeTrialSkipV1'));

const HomeUpgradeSubscriptionV1 = React.lazy(
  () => import('pages/v1/HomeUpgradeSubscriptionV1')
);

const HomeAddPlanV1 = React.lazy(() => import('pages/v1/HomeAddPlanV1'));

const HomeRemovePlanV1 = React.lazy(() => import('pages/v1/HomeRemovePlanV1'));

const UpsellV1 = React.lazy(() => import('pages/v1/UpsellV1'));

const HomeDataOnlyUpsellV1 = React.lazy(
  () => import('pages/v1/DataOnlyUpsellV1')
);

const WhySkipTrialV1 = React.lazy(() => import('pages/v1/WhySkipTrialV1'));

const MyOrdersV1 = React.lazy(() => import('pages/v1/MyOrdersV1'));

const PaymentMethodListingV1 = React.lazy(
  () => import('pages/v1/PaymentMethodListingV1')
);

const RetryPaymentV1 = React.lazy(() => import('pages/v1/RetryPaymentV1'));

const PaymentPollingNStatusV1 = React.lazy(
  () => import('pages/v1/PaymentPollingNStatusV1')
);

// const ReviewDataSubscription = React.lazy(
//   () => import('pages/v1/ReviewDataSubscriptionModifyV1/index')

const ReviewPurchaseV1 = React.lazy(
  () => import('pages/v1/ReviewPurchaseV1/index')
);

const SubscriptionUpgradeBannerV1 = React.lazy(
  () => import('pages/v1/SubscriptionUpgradeBannerV1')
);

const ReviewDowngradeV1 = React.lazy(
  () => import('pages/v1/ReviewDowngradeV1')
);

const UpgradeDataSubscriptionV1 = React.lazy(
  () => import('pages/v1/HomeUpgradeDataSubscriptionV1')
);

const SetupDataAutoTopUpV1 = React.lazy(
  () => import('pages/v1/DataAutoChargeV1/index')
);

const DataAutoTopUpPaymentV1 = React.lazy(
  () => import('pages/v1/DataAutoTopUpPaymentV1/index')
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Redirects a legacy data route to its new equivalent, preserving any path
// params (e.g. :id) so older app builds keep working after the route rename.
function LegacyRouteRedirect({ to }: { to: string }) {
  const params = useParams();
  return (
    <Navigate to={getRoute(to, params as Record<string, string>)} replace />
  );
}

function App() {
  // Get the translated partner config using our custom hook
  const { config, name } = usePartnerConfig();

  // Follows the OS/native-app setting. Paired with src/App.css (the
  // `prefers-color-scheme: dark` icon-inversion block and :root colour-scheme)
  // and the <meta name="color-scheme"> in index.html — see those for the rest
  // of the dark-mode wiring.
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = getPartnerTheme(prefersDarkMode);

  console.log(
    `App running with partner ${name} on environment ${import.meta.env.VITE_ENVIRONMENT}`
  );

  return (
    <div className="App" style={{ height: '100vh' }}>
      <Sentry.ErrorBoundary>
        <ErrorBoundary>
          <ScrollToTop />
          <ConfigContext.Provider value={config}>
            <ThemeProvider theme={theme}>
              {/* Re-themes the whole tree from the URL: /bird/*, /pet/*,
                  /baby/* and /trail/* take their product's colour, page
                  background and gradient; everything else is Home. Sits above
                  CssBaseline so the <body> background follows too. */}
              <RouteProductTheme>
                <CssBaseline />
                <Suspense fallback={<InstaLoader />}>
                  <SentryRoutes>
                    <Route element={<InternetStatusRoute />}>
                      {/* <Route path={ROUTES.LAUNCH_APP} Component={LaunchApp} /> */}
                      <Route path={ROUTES.LOGIN} Component={LoginScreen} />

                      {/* Privacy Policy and TOS Routes */}
                      <Route
                        path={ROUTES.PRIVACY_POLICY}
                        Component={PrivacyPolicy}
                      />
                      <Route
                        path={ROUTES.TERMS_OF_SERVICE}
                        Component={TermsOfService}
                      />

                      {/* Account Delete Routes */}
                      <Route
                        path={ROUTES.REQUEST_DELETE}
                        Component={AccountDeletionRequest}
                      />
                      <Route
                        path={ROUTES.VERIFY_DELETE}
                        Component={VerifyDeleteRequest}
                      />

                      {/* Password Reset Route — public: the user cannot sign in,
                        the emailed token is the only capability */}
                      <Route
                        path={ROUTES.RESET_PASSWORD}
                        Component={ResetPassword}
                      />

                      {/* App Bridge Routes */}
                      <Route
                        path={ROUTES.APP_BRIDGE}
                        Component={AppBridgePage}
                      />

                      {/* Private Routes */}
                      <Route
                        path={ROUTES.APP_QR_SUPPORT}
                        Component={SupportPage}
                      />

                      <Route element={<PrivateRoute />}>
                        {/* Camera Routes */}

                        <Route
                          path={ROUTES.ADD_CAMERA_INFO}
                          Component={AddCameraInfoEntry}
                        />

                        <Route
                          path={ROUTES.ADD_HUB_INFO}
                          Component={AddHubInfoV1}
                        />

                        <Route
                          path={ROUTES.CAMERA_LIMIT_REACHED}
                          Component={MaxCameraCountReached}
                        />

                        {/* Home Subscription Routes */}

                        <Route
                          path={ROUTES.HOME_BUY_SUBSCRIPTION}
                          Component={HomeBuySubscriptionV1}
                        />
                        {/* Deep-linked by the native app; redirect to the
                          canonical /home/* route above. */}
                        <Route
                          path={ROUTES.BUY_SUBSCRIPTION}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.HOME_BUY_SUBSCRIPTION}
                            />
                          }
                        />

                        <Route
                          path={ROUTES.DATA_SUBSCRIPTION}
                          Component={HomeBuyDataSubscriptionV1}
                        />

                        <Route
                          path={ROUTES.ONE_TIME_DATA_PURCHASE}
                          Component={FourGOneTimeDataPlanScreen}
                        />

                        <Route
                          path={ROUTES.UPGRADE_BASIC}
                          Component={SubscriptionUpgradeBannerV1}
                        />

                        <Route
                          path={ROUTES.HOME_SUBSCRIPTION_UPGRADE}
                          Component={HomeUpgradeSubscriptionV1}
                        />
                        {/* Multiple Plan Subscriptions (FCM-2916). Both are
                          deep-linked by the native app. */}
                        <Route
                          path={ROUTES.HOME_ADD_PLAN}
                          Component={HomeAddPlanV1}
                        />
                        <Route
                          path={ROUTES.HOME_REMOVE_PLAN}
                          Component={HomeRemovePlanV1}
                        />
                        {/* Deep-linked by the native app; :id is preserved. */}
                        <Route
                          path={ROUTES.SUBSCRIPTION_UPGRADE}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.HOME_SUBSCRIPTION_UPGRADE}
                            />
                          }
                        />
                        <Route
                          path={ROUTES.SUBSCRIPTION_LIST}
                          Component={HomeSubscriptionListV1}
                        />
                        <Route
                          path={ROUTES.SUBSCRIPTION_DETAIL}
                          Component={SubscriptionDetailsV1}
                        />
                        <Route
                          path={ROUTES.REVIEW_PURCHASE}
                          Component={ReviewPurchaseV1}
                        />
                        <Route
                          path={ROUTES.REVIEW_DOWNGRADE}
                          Component={ReviewDowngradeV1}
                        />

                        <Route
                          path={ROUTES.UPGRADE_DATA_SUBSCRIPTION}
                          Component={UpgradeDataSubscriptionV1}
                        />

                        <Route
                          path={ROUTES.DATA_AUTO_TOP_UP}
                          Component={SetupDataAutoTopUpV1}
                        />

                        <Route
                          path={ROUTES.DATA_AUTO_TOP_UP_PAYMENT}
                          Component={DataAutoTopUpPaymentV1}
                        />

                        {/* Backward-compat: redirect the old `/home/...` data
                          routes (still opened by older app builds) to the new
                          `/data/...` routes above. */}
                        <Route
                          path={ROUTES.LEGACY_DATA_SUBSCRIPTION}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.DATA_SUBSCRIPTION}
                            />
                          }
                        />
                        <Route
                          path={ROUTES.LEGACY_UPGRADE_DATA_SUBSCRIPTION}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.UPGRADE_DATA_SUBSCRIPTION}
                            />
                          }
                        />
                        <Route
                          path={ROUTES.LEGACY_ONE_TIME_DATA_PURCHASE}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.ONE_TIME_DATA_PURCHASE}
                            />
                          }
                        />
                        <Route
                          path={ROUTES.LEGACY_DATA_AUTO_TOP_UP}
                          element={
                            <LegacyRouteRedirect to={ROUTES.DATA_AUTO_TOP_UP} />
                          }
                        />
                        <Route
                          path={ROUTES.LEGACY_DATA_AUTO_TOP_UP_PAYMENT}
                          element={
                            <LegacyRouteRedirect
                              to={ROUTES.DATA_AUTO_TOP_UP_PAYMENT}
                            />
                          }
                        />

                        {/* The base-plan chooser is deprecated; send anyone who
                          lands here straight to the data auto top-up screen. */}
                        <Route
                          path={ROUTES.BASE_PLANS_AUTO_TOP_UP}
                          element={
                            <Navigate to={ROUTES.DATA_AUTO_TOP_UP} replace />
                          }
                        />

                        <Route
                          path={ROUTES.PAYMENT_SUCCESS}
                          Component={PaymentPollingNStatusV1}
                        />

                        {/* Bird Subscription Routes */}

                        <Route
                          path={ROUTES.BIRD_BUY_SUBSCRIPTION}
                          Component={BuyBirdFeederSubscriptionV1}
                        />
                        <Route
                          path={ROUTES.BIRD_SUBSCRIPTION_DETAIL}
                          Component={SubscriptionDetailsV1}
                        />
                        <Route
                          path={ROUTES.BIRD_SUBSCRIPTION_UPGRADE}
                          Component={BirdFeederUpgradeSubscriptionV1}
                        />

                        {/* Pet Subscription Routes */}

                        <Route
                          path={ROUTES.PET_BUY_SUBSCRIPTION}
                          Component={BuyPetCameraSubscriptionV1}
                        />
                        <Route
                          path={ROUTES.PET_SUBSCRIPTION_DETAIL}
                          Component={SubscriptionDetailsV1}
                        />
                        <Route
                          path={ROUTES.PET_SUBSCRIPTION_UPGRADE}
                          Component={PetCameraUpgradeSubscriptionV1}
                        />

                        <Route
                          path={ROUTES.BABY_BUY_SUBSCRIPTION}
                          Component={BuyBabyCameraSubscriptionV1}
                        />
                        <Route
                          path={ROUTES.BABY_SUBSCRIPTION_DETAIL}
                          Component={SubscriptionDetailsV1}
                        />
                        <Route
                          path={ROUTES.BABY_SUBSCRIPTION_UPGRADE}
                          Component={BabyCameraUpgradeSubscriptionV1}
                        />

                        {/* Trail Camera Subscription Routes */}

                        <Route
                          path={ROUTES.TRAIL_CAM_BUY_SUBSCRIPTION}
                          Component={BuyTrailCameraSubscriptionV1}
                        />
                        <Route
                          path={ROUTES.TRAIL_CAM_SUBSCRIPTION_DETAIL}
                          Component={SubscriptionDetailsV1}
                        />

                        <Route
                          path={ROUTES.TRAIL_CAM_SUBSCRIPTION_UPGRADE}
                          Component={TrailCameraUpgradeSubscriptionV1}
                        />
                        {/* Trail Camera Subscription End */}

                        {/* Home, Bird and Pet Promotion Routes */}
                        <Route path={ROUTES.BIRD_UPSELL} Component={UpsellV1} />

                        <Route path={ROUTES.PET_UPSELL} Component={UpsellV1} />

                        <Route
                          path={ROUTES.TRAIL_CAM_UPSELL}
                          Component={UpsellV1}
                        />

                        <Route path={ROUTES.BABY_UPSELL} Component={UpsellV1} />

                        <Route
                          path={ROUTES.PROMOTION}
                          element={
                            <ExperimentRoute
                              experiment={EXPERIMENTS.PROMOTION}
                            />
                          }
                        />

                        {/* Home and bird skip trial routes */}

                        <Route
                          path={ROUTES.SKIP_TRIAL}
                          element={<HomeTrialSkipV1 />}
                        />

                        <Route
                          path={ROUTES.DATA_ONLY_UPSELL}
                          Component={HomeDataOnlyUpsellV1}
                        />

                        <Route
                          path={ROUTES.BIRD_SKIP_TRIAL}
                          Component={SkipTrialV1}
                        />

                        <Route
                          path={ROUTES.PET_SKIP_TRIAL}
                          Component={SkipTrialV1}
                        />

                        <Route
                          path={ROUTES.TRAIL_CAM_SKIP_TRIAL}
                          Component={SkipTrialV1}
                        />

                        <Route
                          path={ROUTES.BABY_SKIP_TRIAL}
                          Component={SkipTrialV1}
                        />

                        <Route
                          path={ROUTES.WHY_SKIP_TRIAL}
                          Component={WhySkipTrialV1}
                        />

                        <Route
                          path={ROUTES.BIRD_WHY_SKIP_TRIAL}
                          Component={WhySkipTrialV1}
                        />

                        <Route
                          path={ROUTES.PET_WHY_SKIP_TRIAL}
                          Component={WhySkipTrialV1}
                        />

                        <Route
                          path={ROUTES.TRAIL_CAM_WHY_SKIP_TRIAL}
                          Component={WhySkipTrialV1}
                        />

                        <Route
                          path={ROUTES.BABY_WHY_SKIP_TRIAL}
                          Component={WhySkipTrialV1}
                        />

                        {/* Stripe Checkout Session routes */}
                        <Route
                          path={ROUTES.CHECKOUT_SESSION}
                          Component={CheckoutScreenV1}
                        />

                        {/* Payment Routes */}
                        <Route
                          path={ROUTES.PAYMENT_METHODS}
                          Component={PaymentMethodListingV1}
                        />
                        <Route
                          path={ROUTES.RETRY_PAYMENT}
                          Component={RetryPaymentV1}
                        />

                        {/* Invoice Routes */}
                        <Route path={ROUTES.BILLINGS} Component={MyOrdersV1} />
                        <Route path={ROUTES.BILLING} Component={MyOrdersV1} />

                        {/* Dashboard Routes */}
                        <Route
                          path={ROUTES.DASHBOARD}
                          Component={DashboardV1}
                        />

                        {/* Support, faqs, cancellation & refund policy routes */}
                        <Route path={ROUTES.FAQ} Component={FAQPage} />
                        <Route
                          path={ROUTES.SUPPORT}
                          Component={CustomerSupport}
                        />
                        <Route
                          path={ROUTES.CANCELLATION_REFUND}
                          Component={CancellationRefundPolicy}
                        />

                        {/* Private routes for account delete routes */}
                        <Route
                          path={ROUTES.REQUEST_DELETE}
                          Component={AccountDeletionRequest}
                        />
                        <Route
                          path={ROUTES.VERIFY_DELETE}
                          Component={VerifyDeleteRequest}
                        />

                        <Route path={ROUTES.NOT_FOUND} Component={NotFound} />
                      </Route>
                      <Route path={ROUTES.NOT_FOUND} Component={LoginScreen} />
                    </Route>
                  </SentryRoutes>
                </Suspense>
              </RouteProductTheme>
            </ThemeProvider>
          </ConfigContext.Provider>
        </ErrorBoundary>
      </Sentry.ErrorBoundary>
    </div>
  );
}

export default App;
